import Enumerable from "linq";
import {
  IComboBoxObjectStyleConfiguration,
  IBackgroundColorConfiguration,
  IBorderColorConfiguration,
  IObjectColorConfiguration,
  IEnableConfiguration,
} from "../../../../domain/configuration";
import {
  PaintStyle,
  TextureKinds,
  TextStyle,
  Color
} from "../../../../domain/drawing";
import {
  ComboBoxItem,
  TextItemOption,
} from "../../../../domain/document/items";
import { AbstractTextObjectStyleReader } from "./AbstractTextObjectStyleReader";
import { FixedColorSource } from ".";

export class ComboBoxObjectStyleReader extends AbstractTextObjectStyleReader<ComboBoxItem> {
  constructor(private readonly _configuration: IComboBoxObjectStyleConfiguration) {
    super();
  }

  protected readSpecifiedPaintStyle(source: ComboBoxItem, shouldInputFree: boolean): PaintStyle {
    if (source.shouldInputFree || shouldInputFree) {
      // フリー入力状態のとき、表示背景色は元の色を維持するが透過色にはならないで白色となる模様
      const color = (source.paintStyle.color.value === "transparent")
        ? new Color("#FFFFFF")
        : source.paintStyle.color;
      return new PaintStyle(color, TextureKinds.Fill);
    }
    // 手動で選択された色が反映されないので対策している
    const selected = this.readSelectedOption(source);
    return (selected || this.hasValidLabel(source)) ? source.paintStyle : null;
  }

  /**
   * V2FATからの移行データで未選択の表示色となってしまうので対策している
   */
  private hasValidLabel(source: ComboBoxItem): boolean {
    return !!source.label && Enumerable.from(source.options).any(o => o.text === source.label);
  }

  private readSelectedOption(source: ComboBoxItem): TextItemOption {
    // 選択無しのオプションは対象外.
    return Enumerable.from(source.options)
      .firstOrDefault(o => o.isSelected && o.name && (!!o.text || !!o.score || !!o.code));
  }

  protected readBackgroundColorConfiguration(source: ComboBoxItem, shouldInputFree: boolean): IBackgroundColorConfiguration {
    return this.readObjectStyleConfiguration(source);
  }
  
  protected readBorderColorConfiguration(source: ComboBoxItem, shouldInputFree: boolean): IBorderColorConfiguration {
    return this.readObjectStyleConfiguration(source);
  }

  private readObjectStyleConfiguration(source: ComboBoxItem): IObjectColorConfiguration {
    if (source.injectedRule && source.injectedRule.isEditDisabled) {
      return FixedColorSource.uneditable;
    }
    return this._configuration.default;
  }

  public readTextStyle(source: ComboBoxItem, shouldInputFree: boolean, isFocused: boolean): TextStyle {
    return ((source.shouldInputFree || (source.isFreeInputable && shouldInputFree)) && isFocused)
      ? this.toFreeInputTextStyle(source)
      : this.toTextStyle(source);
  }

  protected readForegroundDisplayConfiguration(): IEnableConfiguration {
    return this._configuration.foreground;
  }
}
